import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { APP_VERSION } from 'config/constants';
import { setAppVersionNotMatch } from 'redux/appSlice';
import type { TRootState } from 'redux/store';
import { logout } from '../modules/auth/authSlice';
import type { IUserResponse } from '../modules/auth/services/type';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://api-dev.32co.com';
// const BASE_URL = 'http://localhost:8001'
// const BASE_URL = 'http://localhost:8002'
// const BASE_URL = 'http://localhost:8003'
// const BASE_URL = 'http://localhost:8005'
// const BASE_URL = 'http://localhost:8006'

export const VERSION = process.env.REACT_APP_BASE_URL_VERSION || 'v1';
export const REACT_APP_32CO_WEBSITE_LINK = process.env.REACT_APP_32CO_WEBSITE_LINK || 'https://32co.com';

export const formatUrl = (path: string) => {
  const userType = localStorage.getItem('userType');
  let type = '';
  if (userType) {
    type = JSON.parse(userType);
  }

  if (path.includes('conversation')) {
    return `/user/${VERSION}${path}`;
  }
  return `/${type}/${VERSION}${path}`;
};

export interface ISignedURLRequest {
  fileName: any;
  mimeType: any;
  folderName: string;
}

export interface IUploadFileRequest {
  url: string;
  file: any;
}

export interface ISkus {
  createdAt: string;
  description: string;
  discounts: any;
  id: string;
  name: string;
  planPrice: any;
  refinementPrice: any;
  skuId: string;
  status: string;
  updatedAt: string;
  lastUpdatedBy: any;
}

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders: (headers: any, { getState }) => {
    headers.set('x-client-version', APP_VERSION);
    // By default, if we have a token in the store, let's use that for authenticated requests
    const { token } = (getState() as TRootState).auth;
    if (token) {
      headers.set('Authorization', token);
    }

    // Add default headers
    headers.set('Content-Type', 'application/json');
    headers.set('Accept', '*/*');

    return headers;
  },
});
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    api.dispatch(logout());
    // try to get a new token
    // const refreshResult = await baseQuery('/refreshToken', api, extraOptions)
    // if (refreshResult.data) {
    //   // store the new token
    //   api.dispatch(tokenReceived(refreshResult.data))
    //   // retry the initial query
    //   result = await baseQuery(args, api, extraOptions)
    // }
    // else {
    //   api.dispatch(loggedOut())
    // }
  } else if ((result.data as any)?.error === 'APP_VERSION_NOT_MATCH') {
    api.dispatch(setAppVersionNotMatch({ isAppVersionNotMatch: true }));
    throw Error('APP_VERSION_NOT_MATCH');
  }
  return result;
};

// initialize an empty api service that we'll inject endpoints into later as needed
export const api = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Patients',
    'Proposals',
    'Modifications',
    'CaseInvitations',
    'Discounts',
    'Notes',
    'TreatmentSubmissions',
    'EduContent',
  ],
  endpoints: (builder) => ({
    getSignedUrl: builder.mutation<any, ISignedURLRequest>({
      query: (credentials) => ({
        url: formatUrl('/signed-url'),
        method: 'POST',
        body: credentials,
      }),
    }),
    uploadFile: builder.mutation<any, IUploadFileRequest>({
      query: (credentials) => ({
        url: credentials.url,
        method: 'PUT',
        body: credentials.file,
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    }),
    getUserProfile: builder.mutation<IUserResponse, any>({
      query: () => `/user/${VERSION}/profile`,
      // providesTags: (result, error, id) => [{ type: 'Patients', id }],

      // This commented code is useful when testing onboarding, please do not remove
      //   transformResponse(
      //     body: any,
      //   ): any {
      //     return {
      //       user: {
      //         ...body.user,
      //         status: 'invited',
      //         isOnboardMeetingBooked: false,
      //         calendlyEventLink: "https://calendly.com/andrey-razuvaev"
      //       },
      //     }
      //   },
    }),
    updateUserProfile: builder.mutation<IUserResponse, any>({
      query: (body) => ({
        url: `/user/${VERSION}/profile`,
        method: 'PUT',
        body,
      }),
    }),
  }),
});

export const {
  useGetSignedUrlMutation,
  useUploadFileMutation,
  useGetUserProfileMutation,
  useUpdateUserProfileMutation,
} = api;
